import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, Mail, Building2, User, Loader2, CheckCircle } from 'lucide-react';

const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/qiil6l84_brand%20stage%20logo.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Email Gate Component for Compliance Documents
function ComplianceEmailGate({ pageType, onAccessGranted }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const pageTitle = pageType === 'terms' ? 'Terms of Service' : pageType === 'privacy' ? 'Privacy Policy' : 'Compliance & Data Protection Summary';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/compliance/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, company, page_type: pageType })
      });

      if (!response.ok) {
        throw new Error('Failed to send access request');
      }

      setSubmitted(true);
    } catch (err) {
      setError('Failed to send email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <div className="bg-green-500/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Check Your Email</h2>
          <p className="text-zinc-400 mb-6">
            We've sent a link to <span className="text-white font-medium">{email}</span> to access our {pageTitle}.
          </p>
          <p className="text-zinc-500 text-sm">
            The link will expire in 24 hours. If you don't see the email, check your spam folder.
          </p>
          <Link to="/" className="inline-block mt-8 text-[#C41E3A] hover:text-white transition-colors">
            ← Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="BrandStage" className="h-10 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white mb-2">Access {pageTitle}</h1>
          <p className="text-zinc-400">
            Enter your email to receive a link to view our compliance documents.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-zinc-400 text-sm mb-2">Email Address *</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your@email.com"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-10 pr-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A]"
              />
            </div>
          </div>

          <div>
            <label className="block text-zinc-400 text-sm mb-2">Your Name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-10 pr-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A]"
              />
            </div>
          </div>

          <div>
            <label className="block text-zinc-400 text-sm mb-2">Company</label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Your Company"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-10 pr-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-[#C41E3A]"
              />
            </div>
          </div>

          {error && (
            <p className="text-red-400 text-sm">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading || !email}
            className="w-full bg-[#C41E3A] hover:bg-[#A01830] disabled:bg-zinc-700 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sending...
              </>
            ) : (
              'Send Access Link'
            )}
          </button>
        </form>

        <p className="text-zinc-500 text-xs text-center mt-6">
          We'll send you a secure link to access the document. Your information helps us understand who is reviewing our compliance materials.
        </p>

        <div className="text-center mt-8">
          <Link to="/" className="text-zinc-400 hover:text-white text-sm transition-colors">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

// Wrapper component that checks for token or shows email gate
function CompliancePageWrapper({ pageType, children }) {
  const [searchParams] = useSearchParams();
  const [accessGranted, setAccessGranted] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/compliance/verify-token/${token}`);
        if (response.ok) {
          const data = await response.json();
          if (data.valid && data.page_type === pageType) {
            setAccessGranted(true);
          } else {
            setError('Invalid token for this page');
          }
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Invalid or expired token');
        }
      } catch (err) {
        setError('Failed to verify access');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token, pageType]);

  if (verifying) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C41E3A] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <div className="bg-red-500/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Access Denied</h2>
          <p className="text-zinc-400 mb-6">{error}</p>
          <Link 
            to={`/legal/${pageType}`}
            className="inline-block bg-[#C41E3A] hover:bg-[#A01830] text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            Request New Access
          </Link>
        </div>
      </div>
    );
  }

  if (!accessGranted) {
    return <ComplianceEmailGate pageType={pageType} onAccessGranted={() => setAccessGranted(true)} />;
  }

  return children;
}

export function TermsPage() {
  const navigate = useNavigate();
  
  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const Section = ({ num, title, children }) => (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-6">
      <h2 className="text-xl font-bold text-white mt-0">{num}. {title}</h2>
      {children}
    </div>
  );

  const P = ({ children }) => <p className="text-zinc-300 mt-3 leading-relaxed">{children}</p>;
  const UL = ({ children }) => <ul className="text-zinc-300 list-disc pl-5 mt-3 space-y-1.5">{children}</ul>;
  
  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <button onClick={handleBack} className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#C41E3A]/20 p-3 rounded-xl">
              <FileText className="w-6 h-6 text-[#C41E3A]" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Terms of Service</h1>
              <p className="text-zinc-400 text-sm">Last updated: October 2026</p>
            </div>
          </div>
          <p className="text-zinc-400 text-sm leading-relaxed">
            These Terms of Service ("Terms") govern your access to and use of the BrandStage platform,
            website, and all related services operated by CONTINEX ("we", "us", "BrandStage").
            By using any BrandStage service you accept these Terms in full.
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-zinc max-w-none">

          <Section num="1" title="Acceptance of Terms">
            <P>
              By accessing or using BrandStage — including creating an account, subscribing to a plan,
              creating or viewing a profile, recording or uploading video content, collecting or providing
              testimonials, or using any feature on the platform — you agree to be bound by these Terms,
              our Privacy Policy, and our Cookie Policy. If you do not agree to these terms, please do not
              use our services.
            </P>
          </Section>

          <Section num="2" title="Services Description">
            <P>
              BrandStage is a video-first professional profile platform that helps businesses and
              professionals present themselves credibly to the wider world. Our services include but
              are not limited to:
            </P>
            <UL>
              <li><strong>Company & Leadership Profiles</strong> — AI-assisted creation, hosting, and management of private, link-only business profiles including company overview, key contact information, product/service showcases, and leadership narratives.</li>
              <li><strong>Video Recording & Processing</strong> — Professional video recording at events and trade shows, Quick Bite video recording (30–40 second branded clips), automated FFmpeg-based video branding, and cloud-hosted video delivery.</li>
              <li><strong>Guest Video Testimonials</strong> — Collection of video testimonials from your customers and partners via a shareable booth link, including consent capture, branding, and display on your profile.</li>
              <li><strong>Subscription Plans & Add-ons</strong> — Tiered subscription plans (Basic, All-Access) and individual Pro feature add-ons that unlock additional profile capabilities such as multi-language translations, email signatures, QR codes, event invitations, and downloadable resources.</li>
              <li><strong>Event Invitations</strong> — Creation and management of event invitations with RSVP tracking, sent to contacts via the platform.</li>
              <li><strong>Email Signatures & QR Codes</strong> — Generation of branded email signature blocks and scannable QR codes linked to your profile.</li>
              <li><strong>ShiftHappens Podcast Library</strong> — Access to curated podcast content hosted on the platform.</li>
              <li><strong>AI-Powered Features</strong> — AI-generated profile content, multi-language translations, and readiness scoring powered by third-party AI services.</li>
              <li><strong>Admin & Analytics</strong> — Dashboard tools for managing profiles, viewing analytics, processing payments, and tracking engagement (available to authorised administrators).</li>
            </UL>
          </Section>

          <Section num="3" title="User Accounts & Registration">
            <P>
              To access most BrandStage services you must create an account. When registering you agree to:
            </P>
            <UL>
              <li>Provide accurate, current, and complete information about yourself and your organisation.</li>
              <li>Maintain and promptly update your registration information.</li>
              <li>Keep your password and account credentials confidential and secure.</li>
              <li>Accept responsibility for all activities that occur under your account.</li>
              <li>Notify us immediately of any unauthorised use of your account.</li>
            </UL>
            <P>
              We reserve the right to suspend or terminate accounts that contain inaccurate information
              or violate these Terms.
            </P>
          </Section>

          <Section num="4" title="Profile Services & Private Access">
            <P>
              BrandStage profiles are <strong>private by design</strong>. Your profile is not indexed by
              search engines and is not publicly discoverable. Only individuals who have your unique
              profile link can view your profile page.
            </P>
            <UL>
              <li>You are solely responsible for deciding who receives your profile link.</li>
              <li>You may update, modify, or unpublish your profile at any time through the profile editor.</li>
              <li>BrandStage does not share profile links with third parties unless explicitly authorised by you.</li>
              <li>Profile content (text, images, videos, documents) must be accurate, lawful, and not misleading.</li>
              <li>BrandStage reserves the right to remove or disable profiles that violate these Terms or contain prohibited content.</li>
            </UL>
          </Section>

          <Section num="5" title="Subscription Plans, Payments & Renewals">
            <P>
              Access to certain features requires an active subscription. By subscribing you agree to the following:
            </P>
            <UL>
              <li><strong>Plans:</strong> BrandStage offers Basic and All-Access subscription tiers, as well as individual Pro feature add-ons. Features and pricing for each tier are described on the Pricing page and may change with reasonable notice.</li>
              <li><strong>Payments:</strong> All payments are processed securely through Razorpay. You agree to provide valid payment information and authorise recurring charges where applicable.</li>
              <li><strong>Invoices:</strong> PDF invoices are generated automatically and may be sent to your registered email address.</li>
              <li><strong>Renewals:</strong> Subscriptions renew at the end of each billing period unless cancelled before the renewal date. We will send renewal reminders via email (and optionally WhatsApp) before your subscription expires.</li>
              <li><strong>Cancellation:</strong> You may cancel your subscription at any time. Cancellation takes effect at the end of the current billing period. No pro-rata refunds are issued for partially used billing periods.</li>
              <li><strong>Coupons & Promotions:</strong> Promotional codes are subject to specific terms, expiry dates, and usage limits. BrandStage reserves the right to void or modify promotions at any time.</li>
              <li><strong>Price Changes:</strong> We may adjust pricing with at least 30 days' advance notice. Continued use after a price change constitutes acceptance.</li>
            </UL>
          </Section>

          <Section num="6" title="Content Rights & Intellectual Property">
            <P>
              By uploading, recording, or submitting content through BrandStage (including but not limited
              to text, images, videos, logos, documents, and testimonials):
            </P>
            <UL>
              <li><strong>Your Ownership:</strong> You retain full ownership of your original content. BrandStage does not claim ownership of user-submitted content.</li>
              <li><strong>Licence to BrandStage:</strong> You grant BrandStage a non-exclusive, worldwide, royalty-free licence to host, store, process, display, and distribute your content solely for the purpose of providing the services you have requested (e.g. displaying your profile, branding your videos, generating email signatures).</li>
              <li><strong>AI-Generated Content:</strong> Content generated by our AI features (profile descriptions, translations, readiness scores) is provided as-is. You are responsible for reviewing and approving AI-generated content before it appears on your profile.</li>
              <li><strong>BrandStage IP:</strong> The BrandStage name, logo, platform design, and proprietary technology remain the intellectual property of CONTINEX. You may not reproduce, modify, or distribute BrandStage IP without written permission.</li>
              <li><strong>Third-Party Content:</strong> You confirm that you have all necessary rights, licences, and permissions for any content you upload or display on your profile.</li>
            </UL>
          </Section>

          <Section num="7" title="Video Recording & Testimonial Consent">
            <P>When participating in video recording sessions or providing/collecting testimonials:</P>
            <UL>
              <li>You consent to being recorded and your video being processed, branded, and displayed by BrandStage as part of the agreed services.</li>
              <li>You authorise the use of your video for the intended business purposes as described on your profile.</li>
              <li>You understand that processed videos may include BrandStage branding elements (intros, outros, lower thirds).</li>
              <li>Guest testimonial providers must give explicit consent before recording. BrandStage captures consent records (timestamp, IP address) in compliance with applicable data protection laws.</li>
              <li>You may request deletion of your videos at any time by contacting us. We will process deletion requests within 30 days.</li>
            </UL>
          </Section>

          <Section num="8" title="Data Protection & Privacy">
            <P>
              BrandStage is committed to protecting your personal data in accordance with the
              Indian Digital Personal Data Protection Act (DPDP Act, 2023) and the EU General Data
              Protection Regulation (GDPR) where applicable.
            </P>
            <UL>
              <li>We collect and process personal data only for the purposes described in our Privacy Policy.</li>
              <li>Consent for data processing, marketing communications, and analytics is captured explicitly during registration and at relevant touchpoints.</li>
              <li>You may withdraw consent, request data export, or request deletion of your personal data at any time.</li>
              <li>For full details on data collection, storage, processing, and your rights, please refer to our <a href="/privacy" className="text-[#C41E3A] hover:underline">Privacy Policy</a>.</li>
            </UL>
          </Section>

          <Section num="9" title="Prohibited Uses">
            <P>You agree not to use BrandStage services:</P>
            <UL>
              <li>For any unlawful purpose or to promote illegal activities.</li>
              <li>To upload or display harmful, offensive, defamatory, or inappropriate content.</li>
              <li>To infringe on the intellectual property rights of others.</li>
              <li>To impersonate another person or organisation or misrepresent your affiliation.</li>
              <li>To attempt to gain unauthorised access to our systems, other user accounts, or data.</li>
              <li>To scrape, crawl, or harvest data from the platform without written permission.</li>
              <li>To distribute spam, malware, or unsolicited communications through the platform.</li>
              <li>To reverse-engineer, decompile, or disassemble any part of the BrandStage platform.</li>
            </UL>
          </Section>

          <Section num="10" title="Third-Party Services & Integrations">
            <P>
              BrandStage integrates with third-party services to deliver certain features. Your use of
              these integrations is subject to their respective terms:
            </P>
            <UL>
              <li><strong>Razorpay</strong> — Payment processing. Subject to Razorpay's terms of service.</li>
              <li><strong>Cloudinary</strong> — Media storage and delivery. Subject to Cloudinary's terms.</li>
              <li><strong>YouTube</strong> — Embedded video playback. Subject to YouTube's Terms of Service.</li>
              <li><strong>AI Services</strong> — Content generation and translation powered by third-party AI providers.</li>
            </UL>
            <P>
              BrandStage is not responsible for the availability, accuracy, or policies of third-party services.
            </P>
          </Section>

          <Section num="11" title="Service Availability & Modifications">
            <P>
              BrandStage strives to maintain reliable, uninterrupted service but does not guarantee
              100% uptime. We may:
            </P>
            <UL>
              <li>Perform scheduled maintenance with reasonable advance notice.</li>
              <li>Modify, update, or enhance features to improve the platform.</li>
              <li>Suspend or discontinue specific features with at least 30 days' notice where practicable.</li>
              <li>Temporarily suspend service in case of security threats or system emergencies.</li>
            </UL>
          </Section>

          <Section num="12" title="Termination & Account Deletion">
            <UL>
              <li><strong>By You:</strong> You may delete your account and associated data at any time by contacting us. Upon deletion, your profile will be unpublished immediately and your data will be permanently erased within 30 days, except where retention is required by law.</li>
              <li><strong>By BrandStage:</strong> We may suspend or terminate your account if you breach these Terms, engage in prohibited conduct, or fail to maintain an active subscription for an extended period. We will provide notice and an opportunity to remedy the breach where reasonable.</li>
              <li><strong>Effect of Termination:</strong> Upon termination, your right to access the platform ceases immediately. Content you have uploaded may be retained for a limited period to allow data export upon request.</li>
            </UL>
          </Section>

          <Section num="13" title="Limitation of Liability">
            <P>
              To the maximum extent permitted by applicable law:
            </P>
            <UL>
              <li>BrandStage shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use our services.</li>
              <li>Our total aggregate liability for any claims arising under these Terms shall not exceed the total amount paid by you to BrandStage in the 12 months preceding the claim.</li>
              <li>BrandStage does not warrant that the platform will be error-free, secure, or uninterrupted.</li>
              <li>We are not liable for any loss of data, revenue, or business opportunities resulting from service interruptions or third-party failures.</li>
            </UL>
          </Section>

          <Section num="14" title="Indemnification">
            <P>
              You agree to indemnify and hold harmless BrandStage, CONTINEX, and their officers, directors,
              employees, and agents from any claims, damages, losses, or expenses (including reasonable
              legal fees) arising from your use of the platform, your content, or your violation of these Terms.
            </P>
          </Section>

          <Section num="15" title="Governing Law & Dispute Resolution">
            <UL>
              <li>These Terms are governed by and construed in accordance with the laws of India.</li>
              <li>Any disputes arising from these Terms shall first be addressed through good-faith negotiation.</li>
              <li>If negotiation fails, disputes shall be resolved through arbitration in Chennai, India, in accordance with the Arbitration and Conciliation Act, 1996.</li>
              <li>Nothing in this clause prevents either party from seeking interim or injunctive relief from a court of competent jurisdiction.</li>
            </UL>
          </Section>

          <Section num="16" title="Changes to These Terms">
            <P>
              We may update these Terms from time to time to reflect changes in our services, legal
              requirements, or business practices. When we make material changes, we will notify you
              via email or a prominent notice on the platform at least 14 days before the changes take
              effect. Your continued use of BrandStage after the effective date constitutes acceptance
              of the revised Terms.
            </P>
          </Section>

          <Section num="17" title="Contact Us">
            <P>
              For questions, concerns, or requests related to these Terms of Service, please contact us:
            </P>
            <div className="mt-3 text-zinc-300">
              <p><strong>Email:</strong> <a href="mailto:operations@brandstage-media.com" className="text-[#C41E3A] hover:underline">operations@brandstage-media.com</a></p>
              <p className="mt-1"><strong>Website:</strong> <a href="https://brandstage-media.com" className="text-[#C41E3A] hover:underline">brandstage-media.com</a></p>
              <p className="mt-1"><strong>Operated by:</strong> CONTINEX</p>
            </div>
          </Section>

        </div>
      </div>
    </div>
  );
}

export function PrivacyPage() {
  const navigate = useNavigate();
  
  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const Section = ({ num, title, children }) => (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-6">
      <h2 className="text-xl font-bold text-white mt-0">{num}. {title}</h2>
      {children}
    </div>
  );

  const P = ({ children }) => <p className="text-zinc-300 mt-3 leading-relaxed">{children}</p>;
  const UL = ({ children }) => <ul className="text-zinc-300 list-disc pl-5 mt-3 space-y-1.5">{children}</ul>;
  
  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <button onClick={handleBack} className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#C41E3A]/20 p-3 rounded-xl">
              <Shield className="w-6 h-6 text-[#C41E3A]" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
              <p className="text-zinc-400 text-sm">Last updated: October 2026</p>
            </div>
          </div>
          <p className="text-zinc-400 text-sm leading-relaxed">
            This Privacy Policy explains how BrandStage, operated by CONTINEX ("we", "us"), collects, uses,
            stores, and protects your personal data when you use our platform, website, and services.
            We are committed to transparency and to complying with the Indian Digital Personal Data
            Protection Act (DPDP Act, 2023) and the EU General Data Protection Regulation (GDPR) where applicable.
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-zinc max-w-none">

          <Section num="1" title="Information We Collect">
            <P>We collect the following categories of personal and non-personal data:</P>
            <UL>
              <li><strong>Account Information:</strong> Name, email address, password (hashed), company name, phone number, and role — collected during registration.</li>
              <li><strong>Profile Information:</strong> Company details, tagline, headquarters, founding year, industry, sector, key contact details, leadership information, product/service descriptions, logos, and cover images — provided by you during profile creation.</li>
              <li><strong>Social Media Links:</strong> LinkedIn, Twitter/X, Instagram, YouTube, and website URLs — provided voluntarily for display on your profile.</li>
              <li><strong>Video Content:</strong> Videos recorded through BrandStage (Quick Bites, leadership interviews, testimonials), including associated metadata (recording date, duration, resolution).</li>
              <li><strong>Testimonial Data:</strong> Guest testimonials collected via your profile's booth link, including the guest's name, organisation, consent record (timestamp, IP address), and video content.</li>
              <li><strong>Payment & Subscription Data:</strong> Subscription plan, billing period, payment status, invoice records, and coupon usage. Payment card details are processed securely by Razorpay and are <strong>never stored</strong> on BrandStage servers.</li>
              <li><strong>Consent Records:</strong> Explicit consent captured at sign-up, before video recording, at checkout, and for marketing communications — including timestamp, IP address, and consent type.</li>
              <li><strong>Event & Invitation Data:</strong> Event details, guest lists, RSVP responses, and invitation delivery status for events created through the platform.</li>
              <li><strong>Usage & Analytics Data:</strong> Pages visited, features used, profile view counts, time spent on pages, device type, browser, IP address, and referral sources.</li>
              <li><strong>Communication Data:</strong> Emails sent via the platform (invoice emails, renewal reminders, event invitations) and their delivery/open status.</li>
            </UL>
          </Section>

          <Section num="2" title="Legal Basis for Processing">
            <P>We process your personal data on the following legal grounds:</P>
            <UL>
              <li><strong>Consent:</strong> For marketing communications, analytics cookies, testimonial collection, and video recording. You may withdraw consent at any time.</li>
              <li><strong>Contractual Necessity:</strong> To provide the services you have subscribed to — including profile hosting, video processing, email signature generation, and subscription management.</li>
              <li><strong>Legitimate Interest:</strong> To improve our services, prevent fraud, ensure platform security, and communicate service-related updates (e.g. renewal reminders).</li>
              <li><strong>Legal Obligation:</strong> To comply with tax, accounting, and regulatory requirements, including maintaining invoice records and responding to lawful data requests.</li>
            </UL>
          </Section>

          <Section num="3" title="How We Use Your Information">
            <P>We use the data we collect for the following purposes:</P>
            <UL>
              <li><strong>Service Delivery:</strong> Creating, hosting, and managing your company profile; processing and branding videos; generating email signatures, QR codes, and downloadable resources.</li>
              <li><strong>Subscription Management:</strong> Processing payments, generating invoices, managing plan renewals and upgrades, and applying promotional coupons.</li>
              <li><strong>Communication:</strong> Sending transactional emails (invoice receipts, renewal reminders), event invitations, and marketing communications (with your consent).</li>
              <li><strong>AI-Powered Features:</strong> Generating profile descriptions, translating content into multiple languages, and calculating readiness scores using third-party AI services. Your data is processed by AI solely for the purpose you requested and is not used to train AI models.</li>
              <li><strong>Analytics & Improvement:</strong> Understanding how our services are used, identifying areas for improvement, and measuring engagement with profiles and features.</li>
              <li><strong>Security & Fraud Prevention:</strong> Protecting accounts, detecting suspicious activity, enforcing our Terms of Service, and preventing abuse.</li>
            </UL>
          </Section>

          <Section num="4" title="Profile Privacy & Link-Only Access">
            <P>
              BrandStage profiles are <strong>private by design</strong>. This is a core principle of our platform:
            </P>
            <UL>
              <li>Profiles are <strong>not indexed</strong> by search engines (Google, Bing, etc.) and are not listed in any public directory.</li>
              <li>Only individuals who have your unique profile link can view your profile page.</li>
              <li>You control who receives your profile link. BrandStage does not distribute profile links without your authorisation.</li>
              <li>Profile analytics (view counts, engagement data) are visible only to the profile owner and platform administrators.</li>
              <li>You may unpublish your profile at any time, making it immediately inaccessible to anyone with the link.</li>
            </UL>
          </Section>

          <Section num="5" title="Video Content, Testimonials & Consent">
            <UL>
              <li><strong>Your Videos:</strong> Videos you record or upload are stored securely on Cloudinary. Processed videos may include BrandStage branding (intros, outros, lower thirds) and are delivered to your registered email and displayed on your profile.</li>
              <li><strong>Guest Testimonials:</strong> When guests record testimonials via your booth link, we capture explicit consent (timestamp, IP address, consent text) before recording begins. This consent record is stored in compliance with DPDP and GDPR requirements.</li>
              <li><strong>Video Deletion:</strong> You may request deletion of any video at any time. We will remove the video from our platform and cloud storage within 30 days of the request.</li>
              <li><strong>No Sale of Content:</strong> We do not sell, license, or share your video content with third parties. Your content is used solely for the purposes you have authorised.</li>
            </UL>
          </Section>

          <Section num="6" title="Subscription & Payment Data">
            <UL>
              <li>All payments are processed through <strong>Razorpay</strong>, a PCI-DSS compliant payment gateway. BrandStage does not store credit/debit card numbers, CVVs, or other sensitive payment credentials.</li>
              <li>We retain subscription records (plan type, start/end dates, payment amounts, invoice numbers) for accounting and tax compliance purposes.</li>
              <li>Automated PDF invoices are generated for each transaction and may be sent to your registered email and to platform administrators.</li>
              <li>Coupon and promotional code usage is recorded for analytics and fraud prevention.</li>
            </UL>
          </Section>

          <Section num="7" title="AI-Powered Features & Data Processing">
            <P>BrandStage uses third-party AI services to power certain features:</P>
            <UL>
              <li><strong>Profile Content Generation:</strong> Company descriptions and taglines generated using AI based on information you provide.</li>
              <li><strong>Multi-Language Translations:</strong> Profile content translated into multiple languages using AI translation services.</li>
              <li><strong>Readiness Scoring:</strong> Digital readiness assessments generated based on your profile completeness and content quality.</li>
            </UL>
            <P>
              Data sent to AI services is limited to the specific content required for the feature. We do not
              share your full profile or account data with AI providers. AI-generated content is provided
              as a suggestion — you review and approve all content before it appears on your profile.
            </P>
          </Section>

          <Section num="8" title="Data Storage & Security">
            <P>We implement robust security measures to protect your data:</P>
            <UL>
              <li><strong>Encryption in Transit:</strong> All data transmitted between your browser and our servers is encrypted using TLS/HTTPS.</li>
              <li><strong>Secure Cloud Infrastructure:</strong> Data is stored on secure cloud servers with role-based access controls, automated backups, and monitoring.</li>
              <li><strong>Password Security:</strong> User passwords are hashed using industry-standard algorithms (bcrypt). We never store passwords in plain text.</li>
              <li><strong>Access Controls:</strong> Employee and administrator access to personal data is restricted on a need-to-know basis and protected by authentication.</li>
              <li><strong>Incident Response:</strong> We maintain an incident response plan and will notify affected users and relevant authorities within 72 hours in the event of a data breach, as required by GDPR and DPDP.</li>
            </UL>
          </Section>

          <Section num="9" title="Data Sharing & Third-Party Processors">
            <P>We share data only as necessary to provide our services and with appropriate safeguards:</P>
            <UL>
              <li><strong>Razorpay</strong> (Payment Processing) — Subscription payments, refunds, and invoice generation. <a href="https://razorpay.com/privacy/" className="text-[#C41E3A] hover:underline">Razorpay Privacy Policy</a></li>
              <li><strong>Cloudinary</strong> (Media Storage & Delivery) — Video and image hosting, processing, and CDN delivery. Servers in US/EU.</li>
              <li><strong>MongoDB Atlas</strong> (Database) — Application data storage. Servers in multiple regions with encryption at rest.</li>
              <li><strong>Resend</strong> (Email Delivery) — Transactional and marketing emails. US-based.</li>
              <li><strong>AI Service Providers</strong> — Content generation and translation. Data is processed per-request and not retained by providers for training.</li>
              <li><strong>Google reCAPTCHA</strong> — Bot detection and abuse prevention during form submissions.</li>
            </UL>
            <P>
              We do not sell your personal data to any third party. We may disclose data when required
              by law, court order, or to protect the safety, rights, or property of BrandStage, our users,
              or the public.
            </P>
          </Section>

          <Section num="10" title="Your Rights">
            <P>
              Depending on your location and applicable law (DPDP Act 2023, GDPR), you have the following rights:
            </P>
            <UL>
              <li><strong>Right to Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Right to Correction:</strong> Request correction of inaccurate or incomplete personal data.</li>
              <li><strong>Right to Deletion:</strong> Request deletion of your personal data and account. We will comply within 30 days, except where retention is required by law.</li>
              <li><strong>Right to Data Portability:</strong> Request your data in a structured, machine-readable format for transfer to another service.</li>
              <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for marketing, analytics, or any processing based on consent, at any time without affecting prior processing.</li>
              <li><strong>Right to Restrict Processing:</strong> Request that we limit how we use your data while a complaint or correction request is being resolved.</li>
              <li><strong>Right to Object:</strong> Object to processing based on legitimate interest, including profiling and direct marketing.</li>
              <li><strong>Right to Complain:</strong> Lodge a complaint with the Data Protection Board of India (under DPDP) or your local supervisory authority (under GDPR).</li>
            </UL>
            <P>
              To exercise any of these rights, contact us at <a href="mailto:operations@brandstage-media.com" className="text-[#C41E3A] hover:underline">operations@brandstage-media.com</a> or
              write to our Grievance Officer (see Section 16). We will respond within 30 days.
            </P>
          </Section>

          <Section num="11" title="Cookies & Tracking Technologies">
            <P>
              We use cookies and similar technologies to keep you signed in, remember your preferences,
              understand usage patterns, and improve your experience. We use a cookie consent banner that
              allows you to accept or reject non-essential cookies.
            </P>
            <P>
              For full details on the types of cookies we use, how to manage them, and third-party cookies,
              please refer to our <a href="/legal/cookies" className="text-[#C41E3A] hover:underline">Cookie Policy</a>.
            </P>
          </Section>

          <Section num="12" title="Data Retention">
            <UL>
              <li><strong>Active Accounts:</strong> Data is retained for as long as your account is active and your subscription is valid.</li>
              <li><strong>Inactive Accounts:</strong> If your subscription expires and is not renewed, your profile is unpublished but your data is retained for 12 months to allow reactivation.</li>
              <li><strong>Deleted Accounts:</strong> Upon account deletion request, personal data is permanently erased within 30 days. Anonymised analytics data may be retained.</li>
              <li><strong>Legal Retention:</strong> Invoice records, payment data, and consent records are retained for up to 7 years as required by Indian tax and accounting regulations.</li>
              <li><strong>Video Content:</strong> Videos are deleted from cloud storage within 30 days of a deletion request or account closure.</li>
            </UL>
          </Section>

          <Section num="13" title="International Data Transfers">
            <P>Your data may be transferred to and processed in countries outside India:</P>
            <UL>
              <li><strong>Cloud Storage (Cloudinary):</strong> Videos and images — servers in US/EU.</li>
              <li><strong>Database (MongoDB Atlas):</strong> Application data — servers in multiple regions with encryption at rest.</li>
              <li><strong>Email Services (Resend):</strong> Email delivery — US-based.</li>
              <li><strong>Payment Processing (Razorpay):</strong> Payment data — India-based with international processing capabilities.</li>
            </UL>
            <P>
              For transfers outside India and the EU, we ensure appropriate safeguards are in place,
              including Standard Contractual Clauses (SCCs) where applicable, to protect your data
              in accordance with DPDP and GDPR requirements.
            </P>
          </Section>

          <Section num="14" title="Children's Data">
            <P>
              BrandStage is a business-to-business platform designed for professionals and organisations.
              Our services are not directed at individuals under the age of 18. We do not knowingly collect
              personal data from children. If we become aware that we have collected data from a minor,
              we will delete it promptly.
            </P>
          </Section>

          <Section num="15" title="Automated Decision-Making">
            <P>
              BrandStage uses automated processing for the following purposes:
            </P>
            <UL>
              <li><strong>Readiness Scoring:</strong> An automated assessment of your profile's completeness and digital presence. This score is informational only and does not restrict access to any service.</li>
              <li><strong>AI Content Generation:</strong> Automated generation of profile descriptions and translations. All AI-generated content requires your review and approval before publication.</li>
              <li><strong>Renewal Reminders:</strong> Automated emails triggered by subscription expiry dates.</li>
            </UL>
            <P>
              No automated decision has a legal or similarly significant effect on you. You may request
              human review of any automated output by contacting us.
            </P>
          </Section>

          <Section num="16" title="Grievance Officer (India DPDP Act 2023)">
            <P>
              In accordance with the Digital Personal Data Protection Act, 2023, we have appointed a Grievance Officer 
              to address any concerns or complaints regarding data processing:
            </P>
            <div className="bg-zinc-800/50 rounded-lg p-4 mt-3">
              <p className="text-white font-semibold">Grievance Officer</p>
              <p className="text-zinc-300">Continex Tradeline India Private Limited</p>
              <p className="text-zinc-300">Primus Building, No. SP-7A, 1st Floor</p>
              <p className="text-zinc-300">Guindy Industrial Estate, Chennai - 600032, India</p>
              <p className="text-[#C41E3A] mt-2">Email: grievance@brandstage-media.com</p>
              <p className="text-zinc-400 text-sm mt-2">Response time: Within 30 days of receiving complaint</p>
            </div>
          </Section>

          <Section num="17" title="Changes to This Policy">
            <P>
              We may update this Privacy Policy to reflect changes in our services, data practices,
              or legal requirements. When we make material changes, we will notify you via email or
              a prominent notice on the platform at least 14 days before the changes take effect.
              The "Last updated" date at the top indicates when the policy was last revised.
            </P>
          </Section>

          <Section num="18" title="Contact Us">
            <P>
              For privacy-related questions, data access requests, or to exercise any of your rights, contact us:
            </P>
            <div className="mt-3 text-zinc-300">
              <p><strong>Email:</strong> <a href="mailto:operations@brandstage-media.com" className="text-[#C41E3A] hover:underline">operations@brandstage-media.com</a></p>
              <p className="mt-1"><strong>Grievance Officer:</strong> <a href="mailto:grievance@brandstage-media.com" className="text-[#C41E3A] hover:underline">grievance@brandstage-media.com</a></p>
              <p className="mt-1"><strong>Website:</strong> <a href="https://brandstage-media.com" className="text-[#C41E3A] hover:underline">brandstage-media.com</a></p>
              <p className="mt-1"><strong>Operated by:</strong> Continex Tradeline India Private Limited, Chennai, India</p>
            </div>
          </Section>

        </div>
      </div>
    </div>
  );
}


export function CookiePolicyPage() {
  const navigate = useNavigate();
  
  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };
  
  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <button onClick={handleBack} className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#C41E3A]/20 p-3 rounded-xl">
              <Shield className="w-6 h-6 text-[#C41E3A]" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Cookie Policy</h1>
              <p className="text-zinc-400 text-sm">Last updated: October 2026</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-zinc max-w-none">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-bold text-white mt-0">1. What Are Cookies?</h2>
            <p className="text-zinc-300">
              Cookies are small text files that are placed on your device when you visit our website. 
              They help us provide you with a better experience by remembering your preferences and 
              understanding how you use our services.
            </p>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-bold text-white mt-0">2. Types of Cookies We Use</h2>
            <div className="space-y-4 text-zinc-300">
              <div>
                <h3 className="text-white font-semibold mb-1">Essential Cookies</h3>
                <p>Required for the website to function properly. These cannot be disabled.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">Analytics Cookies</h3>
                <p>Help us understand how visitors interact with our website by collecting anonymous information.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">Functional Cookies</h3>
                <p>Remember your preferences and settings to provide enhanced features.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">Marketing Cookies</h3>
                <p>Used to track visitors across websites to display relevant advertisements.</p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-bold text-white mt-0">3. Managing Cookies</h2>
            <p className="text-zinc-300">
              You can control and manage cookies in various ways:
            </p>
            <ul className="text-zinc-300 list-disc pl-5 mt-2">
              <li>Use our cookie consent banner to accept or reject non-essential cookies</li>
              <li>Adjust your browser settings to block or delete cookies</li>
              <li>Use browser extensions that manage cookie preferences</li>
            </ul>
            <p className="text-zinc-300 mt-3">
              Please note that disabling certain cookies may affect the functionality of our website.
            </p>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-bold text-white mt-0">4. Third-Party Cookies</h2>
            <p className="text-zinc-300">
              We may use third-party services that place cookies on your device, including:
            </p>
            <ul className="text-zinc-300 list-disc pl-5 mt-2">
              <li><strong>Google Analytics:</strong> For website analytics and performance monitoring</li>
              <li><strong>Google reCAPTCHA:</strong> For bot detection and form abuse prevention</li>
              <li><strong>YouTube:</strong> For embedded video playback (ShiftHappens podcast, profile videos)</li>
              <li><strong>Cloudinary:</strong> For media delivery and optimization</li>
              <li><strong>Razorpay:</strong> For secure payment processing</li>
            </ul>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-bold text-white mt-0">5. Your Rights (GDPR)</h2>
            <p className="text-zinc-300">
              If you are in the European Economic Area (EEA), you have the right to:
            </p>
            <ul className="text-zinc-300 list-disc pl-5 mt-2">
              <li>Access information about cookies we use</li>
              <li>Withdraw consent at any time</li>
              <li>Request deletion of data collected through cookies</li>
              <li>Object to processing of your data</li>
            </ul>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-bold text-white mt-0">6. Contact Us</h2>
            <p className="text-zinc-300">
              If you have questions about our use of cookies, please contact us at:
            </p>
            <p className="text-[#C41E3A] mt-2">operations@brandstage-media.com</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Compliance Summary Page - For Legal Team Reference
export function CompliancePage() {
  const navigate = useNavigate();
  
  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handlePrint = () => {
    window.print();
  };
  
  const content = (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16 print:bg-white print:pt-8">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8 print:mb-4">
          <div className="flex items-center justify-between mb-6 print:hidden">
            <button onClick={handleBack} className="inline-flex items-center gap-2 text-zinc-400 hover:text-white">
              <ArrowLeft className="w-4 h-4" />
              Go Back
            </button>
            <button 
              onClick={handlePrint}
              className="inline-flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              <FileText className="w-4 h-4" />
              Print / Save PDF
            </button>
          </div>
          
          <div className="flex items-center gap-4 mb-4">
            <img 
              src="https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/qiil6l84_brand%20stage%20logo.png" 
              alt="BrandStage" 
              className="h-10 print:h-8"
            />
            <div>
              <h1 className="text-2xl font-bold text-white print:text-black">Compliance & Data Protection Summary</h1>
              <p className="text-zinc-400 text-sm print:text-gray-600">Document Version 1.0 | September 2026</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6 print:space-y-4">
          
          {/* Company Information */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 print:bg-gray-50 print:border-gray-300">
            <h2 className="text-lg font-bold text-white print:text-black mb-3">1. Company Information</h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-zinc-400 print:text-gray-500">Legal Entity</p>
                <p className="text-white print:text-black font-medium">Continex Tradeline India Private Limited</p>
              </div>
              <div>
                <p className="text-zinc-400 print:text-gray-500">Brand Name</p>
                <p className="text-white print:text-black font-medium">BrandStage</p>
              </div>
              <div>
                <p className="text-zinc-400 print:text-gray-500">GST Number</p>
                <p className="text-white print:text-black font-medium">33AADCC0301L1Z9</p>
              </div>
              <div>
                <p className="text-zinc-400 print:text-gray-500">TIN</p>
                <p className="text-white print:text-black font-medium">33061522910</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-zinc-400 print:text-gray-500">Registered Address</p>
                <p className="text-white print:text-black font-medium">Primus Building, No. SP-7A, 1st Floor, Guindy Industrial Estate, Chennai - 600032, India</p>
              </div>
            </div>
          </div>

          {/* Regulatory Compliance */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 print:bg-gray-50 print:border-gray-300">
            <h2 className="text-lg font-bold text-white print:text-black mb-3">2. Regulatory Compliance Status</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-700 print:border-gray-300">
                    <th className="text-left py-2 text-zinc-400 print:text-gray-600 font-medium">Regulation</th>
                    <th className="text-left py-2 text-zinc-400 print:text-gray-600 font-medium">Applicability</th>
                    <th className="text-left py-2 text-zinc-400 print:text-gray-600 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-300 print:text-gray-700">
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">India DPDP Act 2023</td>
                    <td className="py-2">Primary - Indian Operations</td>
                    <td className="py-2"><span className="bg-blue-500/20 text-blue-400 print:bg-blue-100 print:text-blue-700 px-2 py-0.5 rounded text-xs font-medium">Framework Implemented</span></td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">IT Act 2000 (India)</td>
                    <td className="py-2">Primary - Indian Operations</td>
                    <td className="py-2"><span className="bg-blue-500/20 text-blue-400 print:bg-blue-100 print:text-blue-700 px-2 py-0.5 rounded text-xs font-medium">Framework Implemented</span></td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">GDPR (EU)</td>
                    <td className="py-2">If serving EU customers</td>
                    <td className="py-2"><span className="bg-amber-500/20 text-amber-400 print:bg-amber-100 print:text-amber-700 px-2 py-0.5 rounded text-xs font-medium">Applicable Where Relevant</span></td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">PCI-DSS</td>
                    <td className="py-2">Payment Processing</td>
                    <td className="py-2"><span className="bg-blue-500/20 text-blue-400 print:bg-blue-100 print:text-blue-700 px-2 py-0.5 rounded text-xs font-medium">Via Razorpay</span></td>
                  </tr>
                  <tr>
                    <td className="py-2 font-medium text-white print:text-black">GST Compliance</td>
                    <td className="py-2">Tax Compliance</td>
                    <td className="py-2"><span className="bg-green-500/20 text-green-400 print:bg-green-100 print:text-green-700 px-2 py-0.5 rounded text-xs font-medium">Registered</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Data Collection */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 print:bg-gray-50 print:border-gray-300">
            <h2 className="text-lg font-bold text-white print:text-black mb-3">3. Data Collection & Processing</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-700 print:border-gray-300">
                    <th className="text-left py-2 text-zinc-400 print:text-gray-600 font-medium">Data Category</th>
                    <th className="text-left py-2 text-zinc-400 print:text-gray-600 font-medium">Data Fields</th>
                    <th className="text-left py-2 text-zinc-400 print:text-gray-600 font-medium">Purpose</th>
                    <th className="text-left py-2 text-zinc-400 print:text-gray-600 font-medium">Legal Basis</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-300 print:text-gray-700">
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Account Data</td>
                    <td className="py-2">Name, Email, Password (hashed), Phone, Country</td>
                    <td className="py-2">Account management, Authentication</td>
                    <td className="py-2">Contract performance</td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Company Profile</td>
                    <td className="py-2">Company name, Address, Website, Social links, Logo</td>
                    <td className="py-2">Profile creation, Public display</td>
                    <td className="py-2">Consent</td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Video Content</td>
                    <td className="py-2">Recorded videos, Testimonials</td>
                    <td className="py-2">Service delivery, Profile enhancement</td>
                    <td className="py-2">Consent</td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Payment Data</td>
                    <td className="py-2">Transaction IDs only (card data via Razorpay)</td>
                    <td className="py-2">Payment processing, Invoicing</td>
                    <td className="py-2">Contract performance</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-medium text-white print:text-black">Lead Data</td>
                    <td className="py-2">Name, Email, Company, Message</td>
                    <td className="py-2">Sales inquiries, Marketing</td>
                    <td className="py-2">Legitimate interest</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Third-Party Services */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 print:bg-gray-50 print:border-gray-300">
            <h2 className="text-lg font-bold text-white print:text-black mb-3">4. Third-Party Service Providers</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-700 print:border-gray-300">
                    <th className="text-left py-2 text-zinc-400 print:text-gray-600 font-medium">Service</th>
                    <th className="text-left py-2 text-zinc-400 print:text-gray-600 font-medium">Provider</th>
                    <th className="text-left py-2 text-zinc-400 print:text-gray-600 font-medium">Data Location</th>
                    <th className="text-left py-2 text-zinc-400 print:text-gray-600 font-medium">Purpose</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-300 print:text-gray-700">
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Video & Image Storage</td>
                    <td className="py-2">Cloudinary</td>
                    <td className="py-2">US / EU</td>
                    <td className="py-2">Media storage & delivery</td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Database</td>
                    <td className="py-2">MongoDB Atlas</td>
                    <td className="py-2">Multi-region</td>
                    <td className="py-2">Data storage</td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Payment Processing</td>
                    <td className="py-2">Razorpay</td>
                    <td className="py-2">India</td>
                    <td className="py-2">Payment gateway (PCI-DSS certified)</td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Email Services</td>
                    <td className="py-2">Resend</td>
                    <td className="py-2">US</td>
                    <td className="py-2">Transactional emails</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-medium text-white print:text-black">AI Services</td>
                    <td className="py-2">OpenAI</td>
                    <td className="py-2">US</td>
                    <td className="py-2">Content generation (opt-in)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Data Subject Rights */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 print:bg-gray-50 print:border-gray-300">
            <h2 className="text-lg font-bold text-white print:text-black mb-3">5. Data Subject Rights & Mechanisms</h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-3">
                <p className="text-white print:text-black font-medium mb-1">Right to Access</p>
                <p className="text-zinc-400 print:text-gray-600">Users can view all their data in dashboard</p>
              </div>
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-3">
                <p className="text-white print:text-black font-medium mb-1">Right to Rectification</p>
                <p className="text-zinc-400 print:text-gray-600">Users can edit profile data anytime</p>
              </div>
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-3">
                <p className="text-white print:text-black font-medium mb-1">Right to Erasure</p>
                <p className="text-zinc-400 print:text-gray-600">"Delete Account" button in dashboard</p>
              </div>
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-3">
                <p className="text-white print:text-black font-medium mb-1">Right to Data Portability</p>
                <p className="text-zinc-400 print:text-gray-600">Export data via support request</p>
              </div>
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-3">
                <p className="text-white print:text-black font-medium mb-1">Right to Withdraw Consent</p>
                <p className="text-zinc-400 print:text-gray-600">Cookie banner + account deletion</p>
              </div>
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-3">
                <p className="text-white print:text-black font-medium mb-1">Right to Lodge Complaint</p>
                <p className="text-zinc-400 print:text-gray-600">Grievance officer appointed</p>
              </div>
            </div>
          </div>

          {/* Security Measures - Simplified for public */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 print:bg-gray-50 print:border-gray-300">
            <h2 className="text-lg font-bold text-white print:text-black mb-3">6. Security Measures</h2>
            <p className="text-zinc-400 print:text-gray-600 text-sm mb-4">BrandStage implements industry-standard security controls to protect your data:</p>
            <ul className="text-zinc-300 print:text-gray-700 space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-green-400 print:text-green-600 mt-0.5">✓</span>
                <span><strong>Encryption:</strong> All data encrypted in transit and at rest using industry-standard protocols</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 print:text-green-600 mt-0.5">✓</span>
                <span><strong>Authentication:</strong> Secure password storage and session management</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 print:text-green-600 mt-0.5">✓</span>
                <span><strong>Access Control:</strong> Role-based permissions with least-privilege principles</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 print:text-green-600 mt-0.5">✓</span>
                <span><strong>Payment Security:</strong> No card data stored; payments processed by PCI-DSS certified providers</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 print:text-green-600 mt-0.5">✓</span>
                <span><strong>Monitoring:</strong> Security logging and incident detection in place</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-400 print:text-green-600 mt-0.5">✓</span>
                <span><strong>Assessments:</strong> Regular security reviews and vulnerability assessments</span>
              </li>
            </ul>
            <p className="text-zinc-500 text-xs mt-4">Detailed security controls documentation available upon request for enterprise customers.</p>
          </div>

          {/* Data Retention */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 print:bg-gray-50 print:border-gray-300">
            <h2 className="text-lg font-bold text-white print:text-black mb-3">7. Data Retention Schedule</h2>
            <p className="text-zinc-400 print:text-gray-600 text-sm mb-4">We retain data only as long as necessary for the stated purpose or legal requirement:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-700 print:border-gray-300">
                    <th className="text-left py-2 text-zinc-400 print:text-gray-600 font-medium">Data Type</th>
                    <th className="text-left py-2 text-zinc-400 print:text-gray-600 font-medium">Retention Period</th>
                    <th className="text-left py-2 text-zinc-400 print:text-gray-600 font-medium">Justification</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-300 print:text-gray-700">
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Active account data</td>
                    <td className="py-2">Duration of account</td>
                    <td className="py-2 text-zinc-400">Service delivery</td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Deleted account metadata</td>
                    <td className="py-2">90 days post-deletion</td>
                    <td className="py-2 text-zinc-400">Support queries, abuse prevention, accidental deletion recovery</td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Profile content (on deletion)</td>
                    <td className="py-2">30 days grace period, then purged</td>
                    <td className="py-2 text-zinc-400">Recovery window for accidental deletion</td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Video content (on deletion)</td>
                    <td className="py-2">Immediate removal from public, purged within 7 days</td>
                    <td className="py-2 text-zinc-400">CDN cache propagation</td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Raw recordings / Retakes</td>
                    <td className="py-2">7 days after final publish</td>
                    <td className="py-2 text-zinc-400">Quality assurance, re-processing if needed</td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Failed uploads</td>
                    <td className="py-2">24 hours</td>
                    <td className="py-2 text-zinc-400">Temporary processing only</td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Testimonial recordings</td>
                    <td className="py-2">Until removed by profile owner or contributor</td>
                    <td className="py-2 text-zinc-400">Contributor consent-based</td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Lead/Enquiry data</td>
                    <td className="py-2">2 years from submission</td>
                    <td className="py-2 text-zinc-400">Sales cycle, follow-up (consent-based marketing separate)</td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Payment/Invoice records</td>
                    <td className="py-2">7 years</td>
                    <td className="py-2 text-zinc-400">Tax compliance, legal requirement</td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Security/Application logs</td>
                    <td className="py-2">180 days rolling</td>
                    <td className="py-2 text-zinc-400">Regulatory requirement (CERT-In)</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-medium text-white print:text-black">Verification documents</td>
                    <td className="py-2">Duration of verified status + 1 year</td>
                    <td className="py-2 text-zinc-400">Audit trail, dispute resolution</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-zinc-500 text-xs mt-4">Users may request earlier deletion of their data by contacting operations@brandstage-media.com. Certain data may be retained longer where required by law.</p>
          </div>

          {/* Cookie & Analytics Inventory */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 print:bg-gray-50 print:border-gray-300">
            <h2 className="text-lg font-bold text-white print:text-black mb-3">8. Cookie & Analytics Inventory</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-700 print:border-gray-300">
                    <th className="text-left py-2 text-zinc-400 print:text-gray-600 font-medium">Cookie/Tracker</th>
                    <th className="text-left py-2 text-zinc-400 print:text-gray-600 font-medium">Type</th>
                    <th className="text-left py-2 text-zinc-400 print:text-gray-600 font-medium">Purpose</th>
                    <th className="text-left py-2 text-zinc-400 print:text-gray-600 font-medium">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-300 print:text-gray-700">
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">access_token</td>
                    <td className="py-2"><span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded text-xs">Essential</span></td>
                    <td className="py-2">User authentication session</td>
                    <td className="py-2">7 days</td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">cookie_consent</td>
                    <td className="py-2"><span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded text-xs">Essential</span></td>
                    <td className="py-2">Remember consent preferences</td>
                    <td className="py-2">1 year</td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">Cloudinary</td>
                    <td className="py-2"><span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded text-xs">Functional</span></td>
                    <td className="py-2">Video/image delivery optimization</td>
                    <td className="py-2">Session</td>
                  </tr>
                  <tr className="border-b border-zinc-800 print:border-gray-200">
                    <td className="py-2 font-medium text-white print:text-black">YouTube Embeds</td>
                    <td className="py-2"><span className="bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded text-xs">Third-Party</span></td>
                    <td className="py-2">Embedded video playback</td>
                    <td className="py-2">Varies (Google policy)</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-medium text-white print:text-black">Razorpay</td>
                    <td className="py-2"><span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded text-xs">Functional</span></td>
                    <td className="py-2">Payment processing (on checkout)</td>
                    <td className="py-2">Session</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-zinc-500 text-xs mt-3">Note: No advertising or marketing trackers are used. Analytics are server-side only.</p>
          </div>

          {/* Product Disclaimers */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 print:bg-gray-50 print:border-gray-300">
            <h2 className="text-lg font-bold text-white print:text-black mb-3">9. Product-Specific Disclaimers</h2>
            <div className="space-y-4 text-sm">
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-4">
                <p className="text-white print:text-black font-semibold mb-2">AI-Assisted Content</p>
                <p className="text-zinc-300 print:text-gray-700">"AI-assisted content is generated from information supplied by the profile owner and/or authorised sources. The profile owner is responsible for reviewing and approving its accuracy before publication. AI-generated content is clearly labelled."</p>
              </div>
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-4">
                <p className="text-white print:text-black font-semibold mb-2">BrandStage Verified Badge</p>
                <p className="text-zinc-300 print:text-gray-700">"BrandStage verification establishes specified identity, organisation association and/or content provenance checks. It does not constitute an endorsement, credit assessment, certification of capability, or guarantee of products or services."</p>
              </div>
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-4">
                <p className="text-white print:text-black font-semibold mb-2">Digital Health Score (Pro)</p>
                <p className="text-zinc-300 print:text-gray-700">"The BrandStage Digital Health Score is an indicative assessment based on defined digital-presence criteria and available information. It is not an SEO guarantee, credit rating, certification, or assurance of commercial performance. Scores are for guidance only."</p>
              </div>
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-4">
                <p className="text-white print:text-black font-semibold mb-2">Video Testimonials & Contributor Consent</p>
                <p className="text-zinc-300 print:text-gray-700">"Testimonials represent the personal views and experiences of the contributor at the time of recording. BrandStage does not independently verify or warrant the claims made. All testimonial contributors provide explicit consent before recording, including acknowledgment that their video may be displayed on the profile owner's public profile. Contributors may request removal of their testimonial by contacting operations@brandstage-media.com."</p>
              </div>
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-4">
                <p className="text-white print:text-black font-semibold mb-2">BrandStage Connect (Coming Soon)</p>
                <p className="text-zinc-300 print:text-gray-700">"Opportunities and introductions on BrandStage Connect are provided for business discovery purposes only. When contact information is shared between participants, both parties acknowledge that BrandStage acts solely as a facilitator. Publication or introduction does not constitute endorsement, due diligence, credit assessment, or assurance of commercial suitability. Participants must undertake their own legal, financial, and commercial assessment before entering into any transaction or agreement. Note: This feature is not yet live."</p>
              </div>
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-4">
                <p className="text-white print:text-black font-semibold mb-2">Profile Owner Content Warranty</p>
                <p className="text-zinc-300 print:text-gray-700">"By publishing a profile on BrandStage, the profile owner warrants and represents that: (a) all information and media submitted are accurate to the best of their knowledge; (b) they have obtained all necessary rights, permissions, and consents to publish the content; (c) the content does not infringe any third-party intellectual property, privacy, or other rights; (d) they will promptly update or remove any content that becomes inaccurate or for which rights have been withdrawn. The profile owner assumes full responsibility for all content published under their profile."</p>
              </div>
            </div>
          </div>

          {/* CERT-In Compliance - Simplified */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 print:bg-gray-50 print:border-gray-300">
            <h2 className="text-lg font-bold text-white print:text-black mb-3">10. CERT-In Cybersecurity Compliance</h2>
            <p className="text-zinc-400 print:text-gray-600 text-sm mb-4">BrandStage maintains compliance with CERT-In Section 70B Directions (April 2022) for cybersecurity incident reporting and log management.</p>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-4">
                <p className="text-white print:text-black font-semibold mb-2">Incident Reporting</p>
                <p className="text-zinc-400 print:text-gray-600 text-sm">Qualifying cyber incidents reported to CERT-In within statutory timelines as required by law.</p>
              </div>
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-4">
                <p className="text-white print:text-black font-semibold mb-2">Log Management</p>
                <p className="text-zinc-400 print:text-gray-600 text-sm">ICT system logs maintained for 180 days with time synchronisation as per regulatory requirements.</p>
              </div>
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-4">
                <p className="text-white print:text-black font-semibold mb-2">Access Controls</p>
                <p className="text-zinc-400 print:text-gray-600 text-sm">Role-based access with least-privilege principles. Security controls continuously improved.</p>
              </div>
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-4">
                <p className="text-white print:text-black font-semibold mb-2">Security Assessments</p>
                <p className="text-zinc-400 print:text-gray-600 text-sm">Regular security reviews conducted. VAPT audits planned for enterprise scale.</p>
              </div>
            </div>
            <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-4">
              <p className="text-white print:text-black font-semibold">CERT-In Point of Contact</p>
              <p className="text-zinc-300 print:text-gray-700 mt-1">Operations & Compliance Team</p>
              <p className="text-zinc-300 print:text-gray-700 mt-2">Email: <span className="text-[#C41E3A]">operations@brandstage-media.com</span></p>
            </div>
          </div>

          {/* Data Practices Q&A - Simplified for public */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 print:bg-gray-50 print:border-gray-300">
            <h2 className="text-lg font-bold text-white print:text-black mb-3">11. Frequently Asked Questions</h2>
            <p className="text-zinc-400 print:text-gray-600 text-sm mb-4">Common questions about how we handle your data</p>
            <div className="space-y-4 text-sm">
              <div className="border-b border-zinc-800 print:border-gray-200 pb-3">
                <p className="text-white print:text-black font-medium">Q: Where is my data stored?</p>
                <p className="text-zinc-300 print:text-gray-700">A: Data is stored in secure cloud infrastructure with encryption at rest and in transit. Media content is stored via Cloudinary's global CDN.</p>
              </div>
              <div className="border-b border-zinc-800 print:border-gray-200 pb-3">
                <p className="text-white print:text-black font-medium">Q: Is AI used to generate content?</p>
                <p className="text-zinc-300 print:text-gray-700">A: Yes, AI assists in generating profile narratives from information you provide. You review and approve all content before publication. AI-generated content is clearly labelled.</p>
              </div>
              <div className="border-b border-zinc-800 print:border-gray-200 pb-3">
                <p className="text-white print:text-black font-medium">Q: What cookies does the site use?</p>
                <p className="text-zinc-300 print:text-gray-700">A: Essential cookies only for authentication and consent preferences. No advertising or marketing trackers. Third-party cookies from YouTube embeds and payment processing only.</p>
              </div>
              <div className="border-b border-zinc-800 print:border-gray-200 pb-3">
                <p className="text-white print:text-black font-medium">Q: Can I delete my data?</p>
                <p className="text-zinc-300 print:text-gray-700">A: Yes. You can delete individual videos, testimonials, and your entire account from the Dashboard. Account deletion removes your data per our retention schedule.</p>
              </div>
              <div className="border-b border-zinc-800 print:border-gray-200 pb-3">
                <p className="text-white print:text-black font-medium">Q: How are testimonial contributors protected?</p>
                <p className="text-zinc-300 print:text-gray-700">A: Contributors must provide explicit consent before recording. They can request removal of their testimonial at any time by contacting us.</p>
              </div>
              <div className="pb-3">
                <p className="text-white print:text-black font-medium">Q: Who can I contact with data protection questions?</p>
                <p className="text-zinc-300 print:text-gray-700">A: Contact our Operations & Compliance team at operations@brandstage-media.com. We respond to all data protection enquiries within 30 days.</p>
              </div>
            </div>
          </div>

          {/* Grievance & Incident Handling */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 print:bg-gray-50 print:border-gray-300">
            <h2 className="text-lg font-bold text-white print:text-black mb-3">12. Grievance & Incident Handling</h2>
            <p className="text-zinc-400 print:text-gray-600 text-sm mb-4">Different categories of concerns are handled through appropriate channels:</p>
            
            <div className="space-y-4">
              {/* Privacy & Data Protection */}
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-4">
                <p className="text-white print:text-black font-semibold mb-2">Privacy & Data Protection Concerns</p>
                <p className="text-zinc-400 print:text-gray-600 text-sm mb-2">Data access requests, correction, deletion, consent withdrawal under DPDP Act / GDPR</p>
                <p className="text-zinc-300 print:text-gray-700">Email: <span className="text-[#C41E3A]">operations@brandstage-media.com</span></p>
                <p className="text-zinc-500 text-xs mt-1">Response: Within 30 days as per statutory requirement</p>
              </div>
              
              {/* Content & IP Issues */}
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-4">
                <p className="text-white print:text-black font-semibold mb-2">Content, Copyright & IP Issues</p>
                <p className="text-zinc-400 print:text-gray-600 text-sm mb-2">Takedown requests, intellectual property claims, content disputes</p>
                <p className="text-zinc-300 print:text-gray-700">Email: <span className="text-[#C41E3A]">operations@brandstage-media.com</span></p>
                <p className="text-zinc-500 text-xs mt-1">Response: Acknowledgment within 48 hours; Resolution within 15 days</p>
              </div>
              
              {/* Security Incidents */}
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-4">
                <p className="text-white print:text-black font-semibold mb-2">Security Incidents & Vulnerabilities</p>
                <p className="text-zinc-400 print:text-gray-600 text-sm mb-2">Suspected breaches, account compromises, vulnerability reports</p>
                <p className="text-zinc-300 print:text-gray-700">Email: <span className="text-[#C41E3A]">operations@brandstage-media.com</span></p>
                <p className="text-zinc-500 text-xs mt-1">Response: Immediate triage; Escalation per incident severity</p>
              </div>
              
              {/* General Grievance */}
              <div className="bg-zinc-800/50 print:bg-gray-100 rounded-lg p-4">
                <p className="text-white print:text-black font-semibold mb-2">Grievance Officer (DPDP Act 2023)</p>
                <p className="text-zinc-300 print:text-gray-700">Continex Tradeline India Private Limited</p>
                <p className="text-zinc-300 print:text-gray-700 text-sm">Primus Building, No. SP-7A, 1st Floor, Guindy Industrial Estate</p>
                <p className="text-zinc-300 print:text-gray-700 text-sm">Chennai - 600032, Tamil Nadu, India</p>
                <p className="text-zinc-300 print:text-gray-700 mt-2">Email: <span className="text-[#C41E3A]">operations@brandstage-media.com</span></p>
                <p className="text-zinc-500 text-xs mt-1">Statutory Resolution: Within 30 days</p>
              </div>
            </div>
            
            {/* Breach Notification */}
            <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <p className="text-white print:text-black font-semibold mb-2">Data Breach Notification Policy</p>
              <p className="text-zinc-300 print:text-gray-700 text-sm">In the event of a data breach affecting personal data:</p>
              <ul className="text-zinc-400 print:text-gray-600 text-sm mt-2 space-y-1 ml-4">
                <li>• <strong>Statutory Reporting:</strong> CERT-In notified within 6 hours for qualifying cyber incidents</li>
                <li>• <strong>Data Protection Authority:</strong> Notified as required under applicable law</li>
                <li>• <strong>User Notification:</strong> Affected individuals notified within 72 hours where required</li>
              </ul>
            </div>
          </div>

          {/* Legal Documents */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 print:bg-gray-50 print:border-gray-300">
            <h2 className="text-lg font-bold text-white print:text-black mb-3">13. Published Legal Documents</h2>
            <ul className="text-zinc-300 print:text-gray-700 space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#C41E3A]" />
                <span><strong>Terms of Service:</strong> brandstage-media.com/terms</span>
              </li>
              <li className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#C41E3A]" />
                <span><strong>Privacy Policy:</strong> brandstage-media.com/privacy</span>
              </li>
              <li className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#C41E3A]" />
                <span><strong>Cookie Policy:</strong> brandstage-media.com/cookies</span>
              </li>
              <li className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#C41E3A]" />
                <span><strong>Video Guidelines:</strong> brandstage-media.com/video-guidelines</span>
              </li>
            </ul>
          </div>

          {/* Footer */}
          <div className="text-center text-zinc-500 print:text-gray-500 text-sm pt-4 border-t border-zinc-800 print:border-gray-300">
            <p>This document is for internal reference and legal review purposes.</p>
            <p>Last updated: September 2026 | Document version: 1.0</p>
          </div>
        </div>
      </div>
    </div>
  );
  
  return (
    <CompliancePageWrapper pageType="compliance">
      {content}
    </CompliancePageWrapper>
  );
}
